import { type ActionCodeSettings, type User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { PublicAuthUser } from '../../types';
import { assertFirestore } from '../firebase';

export type UserProfile = {
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
};

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function appOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export function signInUrl(): string {
  return `${appOrigin()}/tools/general/invoice-generator/sign-in/`;
}

export function actionCodeSettings(continueUrl: string): ActionCodeSettings {
  return {
    url: continueUrl,
    handleCodeInApp: false,
  };
}

function emailIndexRef(email: string) {
  return doc(assertFirestore(), 'emailIndex', email);
}

export async function upsertEmailIndex(
  email: string,
  verified: boolean,
): Promise<void> {
  try {
    await setDoc(
      emailIndexRef(email),
      { email, verified, updatedAt: new Date().toISOString() },
      { merge: true },
    );
  } catch (error) {
    console.warn('Failed to write email index (check Firestore rules):', error);
  }
}

export async function isEmailVerifiedInIndex(
  email: string,
): Promise<boolean | null> {
  try {
    const snap = await getDoc(emailIndexRef(email));
    if (!snap.exists()) return null;
    const verified = snap.data()?.verified;
    if (verified === true) return true;
    if (verified === false) return false;
    return null;
  } catch (error) {
    console.warn('Failed to read email index:', error);
    return null;
  }
}

export async function readProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snap = await getDoc(doc(assertFirestore(), 'users', uid));
    if (!snap.exists()) return null;
    return snap.data() as UserProfile;
  } catch (error) {
    console.warn('Failed to read user profile:', error);
    return null;
  }
}

export function userDoc(uid: string) {
  return doc(assertFirestore(), 'users', uid);
}

/** Persist Auth verification flag on the user profile + email index. */
export async function syncEmailVerified(user: User): Promise<void> {
  const email = normalizeEmail(user.email || '');
  const verified = Boolean(user.emailVerified);
  if (email) await upsertEmailIndex(email, verified);
  try {
    await setDoc(
      userDoc(user.uid),
      {
        ...(email ? { email } : {}),
        emailVerified: verified,
        updatedAt: new Date().toISOString(),
      },
      { merge: true },
    );
  } catch (error) {
    console.warn('Failed to sync emailVerified on profile:', error);
  }
}

export async function toPublic(user: User): Promise<PublicAuthUser> {
  const profile = await readProfile(user.uid);
  const display = (user.displayName || '').trim();
  const [firstFromDisplay, ...rest] = display.split(/\s+/);
  return {
    id: user.uid,
    email: user.email || profile?.email || '',
    firstName: profile?.firstName || firstFromDisplay || '',
    lastName: profile?.lastName || rest.join(' ') || '',
    emailVerified: profile?.emailVerified ?? user.emailVerified,
    createdAt:
      profile?.createdAt ||
      user.metadata.creationTime ||
      new Date().toISOString(),
    updatedAt:
      profile?.updatedAt ||
      user.metadata.lastSignInTime ||
      new Date().toISOString(),
  };
}
